/*
 * Copyright (c) 2024 CalAmp Corp. All Rights Reserved
 */
import React, {useCallback} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import {translate} from '@ion/core/src/locale/locale';
import Tooltip from '@ion/dls/src/components/tooltip/Tooltip';
import DeviceNoGroupsOptionIcon from '@ion/dls/src/icons/DeviceNoGroupsOptionIcon';
import {dlsColor} from '@ion/dls/src/theme/theme';
import {DLSTheme} from '@ion/dls/src/types/types';

export interface DisplayNoGroupsButtonProps {
  groupFilterActive: boolean;
  onQueryRemove: (key: string) => () => void;
  onSelection: (group: {name: string, id: number}) => () => void;
}

const useStyles = makeStyles((theme: DLSTheme) => {
  return {
    noGroups : {
      display        : 'flex',
      justifyContent : 'center',
      alignItems     : 'center',
      cursor         : 'pointer',
      padding        : '4px',
      borderRadius   : '4px',
      transition     : 'background-color 0.3s cubic-bezier(0.4, 0.0, 0.2, 1)',
      '&:hover'      : {
        backgroundColor : ({groupFilterActive}: {groupFilterActive: boolean}) => dlsColor(`${!groupFilterActive ? 'paleBlue' : 'grey6'}`)
      }
    },
    iconWrapper : {
      display        : 'flex',
      alignItems     : 'center',
      justifyContent : 'center'
    },
    tooltip : {
      padding : '12px'
    }
  };
});

/**
 * @DisplayNoGroupsButton
 *
 * This component is used to display the 'No Group' button in the Group Search Input.
 * The button is used to give the user an option to apply a filter to show only Devices
 * that are not associated with any Group.
 *
 * If the 'No Group' filter is active, the button will display a tooltip to remove the filter.
 *
 * ```typescript
 * import DisplayNoGroupsButton from './groups/DisplayNoGroupsButton';
 *
 * ...
 *
 * <Input>
 * ...
 *   <DisplayNoGroupsButton noGroupFilterActive={noGroupFilterActive}
 *    onQueryRemove={onQueryRemove}
 *    onSelection={onSelection} />
 * ...
 * </Input>
 *
 * ```
 *
 * @noGroupFilterActive - A boolean value to determine if the 'No Group' filter is active.
 * @onQueryRemove       - A function to remove the query filter.
 * @onSelection         - A function to apply the 'No Group' filter.
 */
const DisplayNoGroupsButton = ({
  groupFilterActive,
  onQueryRemove,
  onSelection
}: DisplayNoGroupsButtonProps) => {
  const classes      = useStyles({groupFilterActive});
  const color        = groupFilterActive ? dlsColor('grey3') : dlsColor('aaBlue');
  const tooltipTitle = translate(`common:modularsearch.${groupFilterActive ? 'removeGroupFilter' : 'showNoGroupFilter'}`);

  const handleButtonClick = useCallback(() => {
    if (groupFilterActive) {
      onQueryRemove('groupIds')();
    } else {
      onSelection({name: translate('common:modularsearch.noGroup'), id: -1})();
    }
  }, [groupFilterActive, onQueryRemove, onSelection]);

  return (
    <div className={classes.noGroups} onClick={handleButtonClick} role={'button'}>
      <Tooltip title={<span className={classes.tooltip}>{tooltipTitle}</span>}
        placement={'bottom'}
        enterDelay={500}
        arrow>
        <div className={classes.iconWrapper}>
          <DeviceNoGroupsOptionIcon color={color} />
        </div>
      </Tooltip>
    </div>
  );
};

export default DisplayNoGroupsButton;
